import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { EmptyStateComponent } from 'components/EmptyStateComponent';
import { Heading } from 'components/Heading';
import { ScoreBadge } from 'components/ScoreBadge';
import { Spacer } from 'components/Spacer';
import { redirectBasedOnPlayState } from 'engine/api';
import { useStore, useIsRoundCompleted, useTasks } from 'engine/store';
import { ChevronRight, ListIcon } from 'lucide-react';
import type { FC } from 'react';
import type { TaskStatus } from 'shared/modelTypes/client';
import type { GameRound } from 'shared/modelTypes/game';

export const Route = createFileRoute('/rounds/')({
	component: RouteComponent,
	beforeLoad: async ({ location }) => {
		const { game, eventStatus } = useStore.getState();

		if (!game) {
			throw redirect({
				to: '/',
				search: {
					redirect: location.href,
				},
			});
		}

		if (eventStatus == 'open') {
			throw redirect({
				to: '/intro',
			});
		}

		redirectBasedOnPlayState(location as unknown as Location);

		if (game.automaticNavigation === 'per-task') {
			throw redirect({ to: '/current-task' });
		}

		if (game.automaticNavigation === 'per-round') {
			throw redirect({ to: '/current-round' });
		}
	},
});

function RouteComponent() {
	const game = useStore((s) => s.game);

	const roundStates = useStore((s) => s.roundStates);
	const taskStates = useStore((s) => s.taskStates);

	const unlockedRounds = game?.rounds.filter((r) => roundStates[r.id]?.unlocked) ?? [];

	function roundPriority(round: GameRound) {
		const statuses = round.tasks.map((t) => taskStates[t.id].status);

		if (statuses.every((s) => s === 'done')) {
			return 2;
		}
		if (statuses.some((s) => s === 'todo')) {
			return 0;
		}
		return 1;
	}

	unlockedRounds.sort((a, b) => roundPriority(a) - roundPriority(b));

	return (
		<>
			{game && unlockedRounds.length > 0 && <RoundList rounds={unlockedRounds} />}
			{game && unlockedRounds.length === 0 && (
				<EmptyStateComponent icon={ListIcon} title="No rounds unlocked" description="You have not unlocked any rounds yet." />
			)}
		</>
	);
}

const RoundList: FC<{ rounds: GameRound[] }> = ({ rounds }) => {
	return (
		<div className="mx-auto flex max-w-screen-sm flex-col gap-2 p-6">
			{rounds.map((round) => (
				<Round round={round} key={round.id}></Round>
			))}
		</div>
	);
};

const Round: FC<{ round: GameRound }> = ({ round }) => {
	const isCompleted = useIsRoundCompleted(round.id);
	const taskStates = useTasks(round.id);
	const stateCounts = taskStates?.reduce(
		(acc, taskState) => {
			if (taskState.unlocked) acc[taskState.status]++;

			return acc;
		},
		{ todo: 0, 'in-progress': 0, done: 0 } as Record<TaskStatus, number>,
	);

	const navigate = useNavigate();
	const setActiveTaskTab = useStore((s) => s.setActiveTaskTab);

	const isTaskInProgress = taskStates?.every((t) => t.status == 'done' || t.status == 'in-progress');

	return (
		<button
			key={round.id}
			onClick={() => {
				// when navigating from the round list, we want to reset the task tab to 'todo'
				// note that we don't do this when navigating some other way
				// because when you press the back button, you want to go back to the same tab you came from
				setActiveTaskTab('todo');
				navigate({ to: '/rounds/$roundId', params: { roundId: round.id }, viewTransition: { types: ['slide-left'] } });
			}}
			data-completed={isCompleted || isTaskInProgress ? '' : undefined}
			data-in-progress={!isCompleted && isTaskInProgress ? '' : undefined}
			className="flex cursor-pointer select-none items-center border p-3 text-left shadow-md skin-bg-button skin-border-button skin-text-button skin-rounded-lg data-[completed]:opacity-40 data-[in-progress]:opacity-40"
		>
			<div className="flex flex-col gap-2">
				<Heading mode="secondary">{round.title}</Heading>
				<ScoreBadge>
					{getRoundPoints(round)} {__`points`}
				</ScoreBadge>
			</div>
			<Spacer />
			{!isCompleted && (
				<span className="whitespace-nowrap">
					{stateCounts?.done ?? 0} / {taskStates?.length}
				</span>
			)}

			<ChevronRight />
		</button>
	);
};

function getRoundPoints(round: GameRound) {
	return round.tasks.reduce((acc, cur) => acc + cur.points, 0);
}
